import React, { useEffect, useState } from 'react';
import { APP_NAME } from '../data/kitEnv';
import { AUTH_INSIGHTS } from '../data/authInsights';
import './AuthShell.css';

const INTERVAL_MS = 7000;

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Split-screen shell for the auth card (/login in all its modes): the form
// on one side, a showcase panel on the other that cycles what this app can
// do over a technical texture. Pure presentation — the card inside is
// untouched; the deck comes from data/authInsights.js (layers may pass
// their own `insights`).
export default function AuthShell({ children, title = `${APP_NAME} · App Kit`, insights = AUTH_INSIGHTS }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [still] = useState(reducedMotion);
  const n = insights.length;
  const go = (d) => setIdx((i) => (i + d + n) % n);

  // Autoplay: one timer per card, dropped while hovered/focused or when the
  // visitor asked for reduced motion (the controls still work by hand).
  useEffect(() => {
    if (paused || still || n < 2) return undefined;
    const t = setTimeout(() => go(1), INTERVAL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, paused, still, n]);

  const card = insights[idx];

  return (
    <div className="au-shell">
      <div className="au-form">
        <div className="au-card m-rise">{children}</div>
      </div>

      <aside
        className={`au-show${still ? ' is-still' : ''}`}
        aria-label="What this app can do"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className="au-bg" aria-hidden="true">
          <span className="au-glow au-glow-a" />
          <span className="au-glow au-glow-b" />
          <Traces />
          <span className="au-scan" />
        </div>

        <div className="au-inner">
          <div className="au-head m-rise" style={{ '--m-delay': '80ms' }}>
            <span className="cs-dot au-mark" />
            {title}
          </div>

          {card && (
            <div className="au-deck" aria-live="polite" aria-atomic="true">
              <article className="au-slide" key={idx}>
                <p className={`au-kicker is-${card.kind}`}>{card.kicker}</p>
                <h2 className="au-title">{card.title}</h2>
                <p className="au-body">{card.body}</p>
              </article>
            </div>
          )}

          {n > 1 && (
            <div className="au-ctl m-rise" style={{ '--m-delay': '200ms' }}>
              <button type="button" className="au-nav" onClick={() => go(-1)} aria-label="Previous highlight">
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <div className="au-dots" role="tablist" aria-label="Highlights">
                {insights.map((it, i) => (
                  <button
                    key={it.title}
                    type="button"
                    role="tab"
                    className={`au-dot${i === idx ? ' is-on' : ''}`}
                    aria-selected={i === idx}
                    aria-label={`${i + 1} of ${n}: ${it.title}`}
                    onClick={() => setIdx(i)}
                  />
                ))}
              </div>
              <button type="button" className="au-nav" onClick={() => go(1)} aria-label="Next highlight">
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <span className="au-count u-tabular">{String(idx + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}</span>
              {!still && (
                <span className="au-progress" aria-hidden="true">
                  <span
                    key={idx}
                    className="au-progress-fill"
                    style={{ animationDuration: `${INTERVAL_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                  />
                </span>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

// Circuit-trace network behind the deck: orthogonal routes with node pads,
// a dash of light running along each route (stroke-dashoffset, the same
// trick as the pipeline rails). Decorative; hidden from AT and frozen
// under reduced motion by styles/motion.css.
function Traces() {
  const routes = [
    'M-20 120 H180 V260 H420 V200 H700',
    'M60 -20 V90 H300 V40 H560 V160 H760',
    'M-20 420 H140 V340 H380 V480 H640 V380 H820',
    'M220 560 V470 H460 V600 H720 V520',
    'M520 -20 V110 H640 V300 H820',
    'M-20 640 H120 V560',
  ];
  const nodes = [[180, 120], [420, 260], [300, 90], [560, 40], [140, 420], [380, 340], [640, 480], [460, 470], [640, 110], [720, 600], [120, 640]];
  return (
    <svg className="au-traces" viewBox="0 0 800 680" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {routes.map((d) => <path key={d} d={d} className="au-trace-base" />)}
      {routes.map((d, i) => (
        <path key={`f${d}`} d={d} className="au-trace" style={{ '--au-delay': `${i * -1.7}s` }} />
      ))}
      {nodes.map(([x, y], i) => (
        <g key={`${x}-${y}`} className="au-node" style={{ '--au-delay': `${i * -0.9}s` }}>
          <circle cx={x} cy={y} r="3" />
          <circle cx={x} cy={y} r="7" className="au-node-ring" />
        </g>
      ))}
    </svg>
  );
}
